<template>
  <span class="subscription-payment-update-button">
    <SloTooltip
      :enabled="disabled"
      :text="'Subscription order is being processed and changes to this order can no longer be made.' | t"
      position="top-right"
    >
      <button
        :disabled="disabled"
        @click="modalOpen = true"
        class="p-0 bg-transparent fw-book c-blue-400 hover:c-blue-700"
      >
        {{ 'Update payment' | t }}
      </button>
    </SloTooltip>
    <transition name="fade-up">
      <SloModal v-if="modalOpen" :maxWidth="500" :form="false" @dismiss="handleCloseModal">
        <template v-slot:title>
          {{ 'Update payment method' | t }}
        </template>
        <SloLoader :loading="paymentDetailsLoading" size="xl" class="mt-md">
          <ul class="payment-details--saved-payment-methods">
            <li
              v-for="(paymentDetail, index) in paymentDetails"
              :key="`payment-detail=${index}`"
              :class="{
                'payment-details--saved-payment-method': true,
                'checked': isPaymentMethodChecked(paymentDetail)
              }"
            >
              <div class="flex items-start gap-md">
                <input
                  type="radio"
                  :id="`payment-detail-${index}`"
                  :value="paymentDetail.id"
                  v-model="paymentDetailId"
                  :checked="isPaymentMethodChecked(paymentDetail)"
                  :disabled="isCardExpired(paymentDetail)"
                />
                <SloIcon
                  :name="paymentDetail?.cardType.name | getCardTypeIconName"
                  size="lg"
                  class="card-icon"
                />
                <div class="saved-payment-method--details">
                  <span class="saved-payment-method--title">
                    <span class="text-capitalize">{{ paymentDetail?.cardType.code }}</span>
                    {{ paymentDetail.cardNumber | getCardNumberLastFourDigits }}
                    <span v-if="paymentDetail?.defaultPayment">({{ 'default' | t }})</span>
                  </span>
                  <span :class="{'card-is-expired c-red-400': isCardExpired(paymentDetail)}">
                    {{ 'Expires' | t }}: {{ paymentDetail?.expiryMonth }}/{{ paymentDetail?.expiryYear }}
                    <span v-if="isCardExpired(paymentDetail)">{{ '(expired)' | t }}</span>
                  </span>
                  <span>{{ paymentDetail?.billingAddress | formatSingleLineAddress }}</span>
                </div>
              </div>
              <SloIcon name="CircleCheckSolid" />
            </li>
          </ul>
          <router-link
            :to="{
              name: 'marketplace-account-payment-add',
              query: {subscription_redirect: $route.fullPath}
            }"
            class="mt-lg block"
          >
            {{ 'Add a new payment option' | t }}
          </router-link>
        </SloLoader>
        <template v-slot:footer>
          <div class="grid grid-cols-1 gap-md w-100 sm:grid-cols-2">
            <SloButton @click="handleCloseModal" :disabled="actionButtonsDisabled" fill="outline">
              {{ 'Discard Changes' | t }}
            </SloButton>
            <SloButton @click="handlePaymentDetailUpdate" :disabled="actionButtonsDisabled">
              <SloLoader :loading="paymentDetailUpdateLoading" size="md" color="white">
                {{ 'Update' | t }}
              </SloLoader>
            </SloButton>
          </div>
        </template>
      </SloModal>
    </transition>
  </span>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import {
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  formatSingleLineAddress,
  isCardExpired
} from '../../main/utils';
import SloModal from '../../../lib/components/SloModal.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloTooltip from '../../../lib/components/SloTooltip.vue';

export default {
  name: 'SubscriptionPaymentUpdateButton',
  components: {
    SloLoader,
    SloButton,
    SloIcon,
    SloModal,
    SloTooltip
  },
  filters: {
    t,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatSingleLineAddress
  },
  props: {
    disabled: {type: Boolean, default: false},
    subscriptionId: {type: String|Number, required: true},
    currentPaymentDetailId: {type: String|Number, required: true}
  },
  data() {
    return {
      modalOpen: false,
      paymentDetailsLoading: true,
      paymentDetailUpdateLoading: false,
      paymentDetails: [],
      paymentDetailId: null
    };
  },
  computed: {
    actionButtonsDisabled() {
      return this.paymentDetailsLoading || this.paymentDetailUpdateLoading;
    }
  },
  watch: {
    async modalOpen(isOpen) {
      if (isOpen) {
        this.paymentDetailId = this.currentPaymentDetailId;

        await marketplaceApi.getCurrentUserSubscriptionPaymentDetails(this.subscriptionId)
          .then(async res => {
            this.paymentDetails = res.data.payments;
            this.paymentDetailsLoading = false;

            if (this.paymentDetails.length === 0) {
              await this.$router.push({name: 'marketplace-account-payment-add'});
            }
          })
          .catch(async() => {
            this.$snotify.error(t('Error loading your payment details'));
            await this.$router.push({name: 'marketplace-account-subscriptions'});
          });
      }
    }
  },
  methods: {
    async handlePaymentDetailUpdate() {
      try {
        this.paymentDetailUpdateLoading = true;
        await marketplaceApi.updateCurrentUserSubscriptionPayment(
          this.subscriptionId,
          this.paymentDetailId
        );

        this.handleCloseModal();
        this.$emit('subscriptionUpdated');
        this.$snotify.success(t('Subscription payment updated'));
      } catch (e) {
        this.paymentDetailUpdateLoading = false;
        this.$snotify.error(t('Unable to update subscription payment'));
      }
    },
    isCardExpired(paymentDetail) {
      return isCardExpired(paymentDetail);
    },
    isPaymentMethodChecked(paymentDetail) {
      if (this.paymentDetailId === paymentDetail.id) {
        return true;
      }
      if (this.paymentDetailId === paymentDetail.id && paymentDetail.defaultPayment) {
        return true;
      }
    },
    handleCloseModal() {
      this.modalOpen = false;
      this.paymentDetailUpdateLoading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.subscription-payment-update-button {
  .payment-details {
    &--saved-payment-methods {
      grid-template-columns: repeat(1, minmax(0, 1fr));
      padding: 0;
      margin-bottom: 0;

      @media (min-width: $screen-md) {
        grid-template-columns: repeat(1, minmax(0, 1fr));
      }
    }

    &--saved-payment-method input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }
  }
}
</style>
